import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSeoMeta, useHead } from '@unhead/react';
import { propertyApi, Property, settingsApi, mapFilterApi, MapFilterChip } from '../services/api';
import { generateCanonicalUrl, shouldNoindex } from '../utils/seoUtils';
import { getPropertyUrl, getPropertyFullUrl } from '../utils/formatters';
import { formatUnits } from '../domain/property/formatters';
import { FilterChip, ErrorState, errorCodeOf } from '../design-system/components';
import { toggleCompare, COMPARE_MAX } from '../domain/compare/compareStore';
import { appendSoldOutMatches, matchesSearch, searchKey } from '../domain/property/searchMatch';
import { useSearchSuggestions, orderBySuggestion } from '../hooks/useSearchSuggestions';
import { useCompare } from '../domain/compare/useCompare';
import {
  isWithinKorea,
  getDisplayStatus, getMarkerLabel,
  computeMarkerScale,
  formatAreaRange,
  applyChipFilter,
} from './map/mapHelpers';
import { createCatEarMarkerSVG, createSpecialMarkerHTML, usesBannerMarker } from './map/markerSvg';
import { clusterProperties } from './map/clustering';
import { MapHeader } from './map/MapHeader';
import { SidebarPanel } from './map/SidebarPanel';
import { BottomSheet } from './map/BottomSheet';
import { CompareFloatingBar } from './map/CompareFloatingBar';
import { PropertyPanel } from './map/PropertyPanel';
import './MapPage.css';

interface MapPageProps {
  showMyLocation?: boolean;
  onMyLocationShown?: () => void;
  onSpecialPropertiesUpdate?: (properties: Property[]) => void;
}

const MapPage: React.FC<MapPageProps> = ({ showMyLocation, onMyLocationShown, onSpecialPropertiesUpdate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [properties, setProperties] = useState<Property[]>([]);
  const [visibleProperties, setVisibleProperties] = useState<Property[]>([]); // 화면에 보이는 현장만

  // 찜(관심단지) — 경쟁사 표준 UX: 카드에서 바로 찜 (2026-07-07 로드맵 #7)
  // /properties/ 응답의 is_favorited로 초기화, 토글은 낙관적 업데이트.
  const [favoriteIds, setFavoriteIds] = useState<Set<number>>(new Set());
  useEffect(() => {
    setFavoriteIds(new Set(properties.filter((p) => (p as any).is_favorited).map((p) => p.id)));
  }, [properties]);

  // 단지 비교함 (2026-07-07 로드맵 #10) — localStorage 스토어 구독
  const compareIds = useCompare();
  const handleToggleCompare = useCallback((e: React.MouseEvent, propertyId: number) => {
    e.preventDefault();
    e.stopPropagation();
    const { full } = toggleCompare(propertyId);
    if (full) {
      alert(`비교는 최대 ${COMPARE_MAX}개까지 담을 수 있어요.`);
    }
  }, []);

  const handleToggleFavorite = useCallback((e: React.MouseEvent, propertyId: number) => {
    e.preventDefault();
    e.stopPropagation(); // 카드 클릭(상세 이동)과 분리
    const token = localStorage.getItem('access_token');
    if (!token) {
      alert('로그인이 필요한 서비스입니다.');
      navigate('/login');
      return;
    }
    // 낙관적 업데이트 → 실패 시 롤백
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      if (next.has(propertyId)) next.delete(propertyId);
      else next.add(propertyId);
      return next;
    });
    import('../services/api').then(({ favoriteApi }) =>
      favoriteApi.toggle(propertyId).catch(() => {
        setFavoriteIds((prev) => {
          const next = new Set(prev);
          if (next.has(propertyId)) next.delete(propertyId);
          else next.add(propertyId);
          return next;
        });
      })
    );
  }, [navigate]);

  // 헤더 자동완성 검색 (사용자 피드백 2026-05-18)
  // ListPageLegacy 의 searchSuggestions 패턴 재사용
  const [searchQuery, setSearchQuery] = useState('');
  const [soldOutList, setSoldOutList] = useState<Property[]>([]);
  const [searchFocused, setSearchFocused] = useState(false);

  // 상태 우선순위: 분양중 > 분양예정 > 입주완료 > 기타
  const statusPriority: Record<string, number> = {
    '분양중': 1,
    '분양예정': 2,
    '입주완료': 3,
  };

  // 자동완성 suggestions — properties (전체 로드된 단지) 기반 매칭, 최대 8개
  // 자동완성 = 서버 토큰 매칭(순서 바뀜·동의어) + 로컬 매칭(띄어쓰기 무시, 즉시 반응)
  // 2026-09-24 대표 제보 '더팰리스트데시앙 검색 안 됨' 대응. 서버가 죽어도 로컬로 계속 찾힌다.
  const suggestedIds = useSearchSuggestions(searchQuery);
  const searchSuggestions = useMemo(() => {
    if (!searchKey(searchQuery)) return [];
    const suggested = new Set(suggestedIds ?? []);
    const hit = properties.filter((p) => suggested.has(Number(p.id)) || matchesSearch(p, searchQuery));
    // 분양완료는 진행 중 현장 뒤에 — 이름 검색일 때만(2026-10-01 대표 승인)
    const all = appendSoldOutMatches(orderBySuggestion(hit, suggestedIds), soldOutList, searchQuery, suggestedIds);
    // 이름이 검색어와 직접 맞는 현장을 먼저 — 서버 자동완성의 느슨한 결과('자이' 포함 타지역)가 8칸을 다 채워
    // 정확히 찾는 현장이 잘리던 문제(2026-10-01 '대명자이'). sort 는 안정 정렬이라 기존 순서는 유지된다.
    return [...all].sort((a, b) => Number(matchesSearch(b, searchQuery)) - Number(matchesSearch(a, searchQuery))).slice(0, 8);
  }, [properties, soldOutList, searchQuery, suggestedIds]);

  const showSearchDropdown = searchFocused && searchQuery.length >= 1 && searchSuggestions.length > 0;

  // suggestion 클릭 → 지도 해당 좌표로 panTo + 줌인
  const handleSuggestionSelect = useCallback((p: Property) => {
    setSearchQuery('');
    setSearchFocused(false);
    if (!mapInstance.current || !p.latitude || !p.longitude) {
      navigate(getPropertyUrl(p));
      return;
    }
    const target = new (window as any).kakao.maps.LatLng(p.latitude, p.longitude);
    mapInstance.current.setLevel(4); // 가까이 zoom in
    mapInstance.current.panTo(target);
  }, [navigate]);

  // 특별현장과 일반현장 분리.
  // 사용자 피드백 2026-05-21: PC 좌측 list 정렬 = 검색량 내림차순 기본.
  // 특별현장도 검색량 순으로 (special_order 는 tiebreaker).
  const { specialProperties, regularProperties } = useMemo(() => {
    const bySearchVolume = (a: any, b: any) => {
      const sva = a?.naver_search_volume ?? 0;
      const svb = b?.naver_search_volume ?? 0;
      if (sva !== svb) return svb - sva;
      return (a?.special_order ?? 9999) - (b?.special_order ?? 9999);
    };
    const special = visibleProperties.filter(p => p.is_special).sort(bySearchVolume);
    const regular = visibleProperties.filter(p => !p.is_special);
    return { specialProperties: special, regularProperties: regular };
  }, [visibleProperties]);

  // 스페셜 현장이 변경될 때 상위 컴포넌트에 알림
  useEffect(() => {
    if (onSpecialPropertiesUpdate) {
      onSpecialPropertiesUpdate(specialProperties);
    }
  }, [specialProperties, onSpecialPropertiesUpdate]);

  // 정렬된 현장 리스트 (사용자 피드백 2026-05-21: 검색량 내림차순 기본).
  // 특별현장 제외 — 특별현장은 special_order 별도.
  const sortedVisibleProperties = useMemo(() => {
    return [...regularProperties].sort((a, b) => {
      // 1차: 네이버 검색량 내림차순 (인기 단지가 상단)
      const sva = (a as any).naver_search_volume ?? 0;
      const svb = (b as any).naver_search_volume ?? 0;
      if (sva !== svb) return svb - sva;

      // 2차 tiebreaker: 가나다순
      return a.title.localeCompare(b.title, 'ko');
    });
  }, [regularProperties]);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  // 정보밀도 패널(Phase 7, additive) — 현장 선택 시 상세 sections 를 오버레이로 렌더.
  // selectedProperty 로직과 독립: 이 값이 있을 때만 패널을 지도 위에 얹는다.
  const [panelPropertyId, setPanelPropertyId] = useState<number | string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [loadErrorInfo, setLoadErrorInfo] = useState<{ code: string; at: Date } | null>(null); // v2 S3 오류코드·시각
  const [mapLoaded, setMapLoaded] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string[]>(['분양중']); // 상태 필터 (기본값: 분양중)
  const [markerIcons, setMarkerIcons] = useState<{ [key: string]: string }>({
    '분양예정': 'https://t1.daumcdn.net/localimg/localimages/07/mapapidoc/marker_yellow.png',
    '분양중': 'https://t1.daumcdn.net/localimg/localimages/07/mapapidoc/marker_blue.png',
    '분양완료': 'https://t1.daumcdn.net/localimg/localimages/07/mapapidoc/marker_green.png',
    '계약완료': 'https://t1.daumcdn.net/localimg/localimages/07/mapapidoc/marker_gray.png',
  });
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const clusterMarkersRef = useRef<any[]>([]); // 클러스터(＋N) 오버레이 — 개별 마커와 분리 관리(누수 정리용)
  const overlayRef = useRef<any>(null);
  // 렌더 함수/클릭 핸들러를 리스너 클로저에서 최신 참조로 호출하기 위한 ref
  const renderMarkersRef = useRef<(() => void) | null>(null);
  // 완판(분양완료) 현장 — 마커로만 표시(카드·검색·묶음 숫자에는 넣지 않음). 2026-09-26 대표: 긴박감
  const soldOutRef = useRef<Property[]>([]);
  const handleMarkerClickRef = useRef<(p: Property) => void>(() => {});
  const userLocationMarkerRef = useRef<any>(null);
  const userLocationOverlayRef = useRef<any>(null);
  const [locatingUser, setLocatingUser] = useState(false);
  const propertiesRef = useRef<Property[]>([]); // 최신 properties 참조용
  const statusFilterRef = useRef<string[]>(['분양중']); // 최신 statusFilter 참조용 (기본값: 분양중)
  const [locationRequested, setLocationRequested] = useState(false);
  const currentZoomLevelRef = useRef<number>(8); // 현재 줌 레벨 추적
  const selectedMarkerIdRef = useRef<number | null>(null); // v2: 선택 마커 scale(1.1) 유지용 (재렌더 후에도)
  const initialPositionRestoredRef = useRef<boolean>(false); // 초기 위치 복원 여부

  // 지도 위치 저장 (세부페이지 이동 전)
  const saveMapPosition = useCallback(() => {
    if (mapInstance.current) {
      const center = mapInstance.current.getCenter();
      const level = mapInstance.current.getLevel();
      sessionStorage.setItem('mapPosition', JSON.stringify({
        lat: center.getLat(),
        lng: center.getLng(),
        level: level
      }));
    }
  }, []);

  // 저장된 지도 위치 복원
  const getSavedMapPosition = useCallback(() => {
    const saved = sessionStorage.getItem('mapPosition');
    if (saved) {
      try {
        const pos = JSON.parse(saved);
        // 한국 밖 좌표가 저장돼 있으면 무시 (백지 지도 방지)
        if (pos && typeof pos.lat === 'number' && typeof pos.lng === 'number' && !isWithinKorea(pos.lat, pos.lng)) {
          sessionStorage.removeItem('mapPosition');
          return null;
        }
        return pos;
      } catch {
        return null;
      }
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 줌 레벨에 따른 마커 스케일 계산
  // 줌인(낮은 레벨) = 크게, 줌아웃(높은 레벨) = 작게
  const getMarkerScale = useCallback((zoomLevel: number, isSpecial: boolean): number => {
    return computeMarkerScale(zoomLevel, isSpecial);
  }, []);

  // 모든 마커의 스케일 업데이트
  const updateMarkerScales = useCallback((zoomLevel: number) => {
    markersRef.current.forEach((marker) => {
      const isSpecial = (marker as any).isSpecial;
      const scale = getMarkerScale(zoomLevel, isSpecial);
      const content = marker.getContent();
      if (content && content.style) {
        content.style.transform = `scale(${scale})`;
        content.style.transformOrigin = 'center bottom';
      }
    });
  }, [getMarkerScale]);

  // ──────────────────────────────────────────
  // 마커/클러스터 렌더 (Phase 9 — 커스텀 그리드 클러스터링)
  // ──────────────────────────────────────────
  // 특별현장은 항상 개별 배너 마커, 일반현장은 현재 레벨 기준 그리드 클러스터링.
  // 밀집 시 ＋N 원형 오버레이로 묶고, 줌인 시 개별로 펼친다.
  // 상태 필터는 여기서 적용(특별현장은 필터 제외, 항상 노출). 좌표 있는 현장만.
  const renderMarkers = useCallback(() => {
    const map = mapInstance.current;
    const kakao = (window as any).kakao;
    if (!map || !kakao || !kakao.maps) return;

    // 기존 개별 마커 제거
    markersRef.current.forEach((m) => m && m.setMap && m.setMap(null));
    markersRef.current = [];
    // 기존 클러스터 오버레이 제거 (누수 방지)
    clusterMarkersRef.current.forEach((m) => m && m.setMap && m.setMap(null));
    clusterMarkersRef.current = [];

    const currentFilter = statusFilterRef.current;
    const currentZoom = currentZoomLevelRef.current;
    const level = typeof map.getLevel === 'function' ? map.getLevel() : currentZoom;

    // 상태 필터 적용 (마커 생성 로직과 동일: business_type 우선 + 입주완료 자동 판단)
    const candidates = propertiesRef.current.filter((property) => {
      if (!property.latitude || !property.longitude) return false;
      if (property.is_special || usesBannerMarker(property)) return true; // 특별현장·배너(활성 혜택)는 필터 제외, 항상 노출
      const isRental = property.business_type === '임대';
      const isMixed = property.business_type === '분양임대';
      const displayStatus = getDisplayStatus(property);
      const markerStatus = isMixed ? '분양임대' : (isRental ? '임대' : displayStatus);
      if (currentFilter.length > 0 && !currentFilter.includes(markerStatus)) return false;
      return true;
    });

    const { clusters, singles } = clusterProperties(candidates, level);

    // 개별 마커 (특별현장 배너 + 일반 고양이귀) — 기존 렌더 로직 그대로
    singles.forEach((property) => {
      if (!property.latitude || !property.longitude) return;
      const position = new kakao.maps.LatLng(property.latitude, property.longitude);
      let markerContent: HTMLDivElement;
      let yAnchor: number;
      let xAnchor: number;
      let zIndex: number;
      // 배너 여부 = 활성 혜택 유무 (대표 지시 2026-09-21, is_special 아님)
      const isBanner = usesBannerMarker(property);
      const initialScale = getMarkerScale(currentZoom, isBanner);

      if (isBanner) {
        markerContent = document.createElement('div');
        markerContent.className = 'special-marker-wrapper';
        markerContent.innerHTML = createSpecialMarkerHTML(property);
        markerContent.style.cursor = 'pointer';
        markerContent.style.transform = `scale(${initialScale})`;
        markerContent.style.transformOrigin = 'center bottom';
        markerContent.style.transition = 'transform 0.15s ease-out';
        yAnchor = 1;
        xAnchor = 0.5;
        zIndex = 200;
      } else {
        const displayStatus = getDisplayStatus(property);
        const isRental = property.business_type === '임대';
        const isMixed = property.business_type === '분양임대';
        const markerLabel = isMixed ? '분양임대' : (isRental ? '임대' : getMarkerLabel(property));
        const markerStatus = isMixed ? '분양임대' : (isRental ? '임대' : displayStatus);
        markerContent = document.createElement('div');
        markerContent.className =
          selectedMarkerIdRef.current === property.id ? 'cat-ear-marker is-selected' : 'cat-ear-marker';
        markerContent.innerHTML = createCatEarMarkerSVG(markerStatus, markerLabel);
        markerContent.style.cursor = 'pointer';
        markerContent.style.transform = `scale(${initialScale})`;
        markerContent.style.transformOrigin = 'center bottom';
        markerContent.style.transition = 'transform 0.15s ease-out';
        yAnchor = 1;
        xAnchor = 0.6;
        zIndex = 10;
      }

      const marker = new kakao.maps.CustomOverlay({
        position,
        content: markerContent,
        yAnchor,
        xAnchor,
        clickable: true,
        zIndex,
      });
      markerContent.addEventListener('click', () => {
        handleMarkerClickRef.current(property);
      });
      marker.setMap(map);
      (marker as any).propertyData = property;
      (marker as any).isSpecial = isBanner; // updateMarkerScales 가 배너/일반 스케일 구분에 사용
      markersRef.current.push(marker);
    });

    // 클러스터 마커 (원형 ＋N, Concept A 토큰)
    clusters.forEach((cluster) => {
      const position = new kakao.maps.LatLng(cluster.lat, cluster.lng);
      const scale = getMarkerScale(currentZoom, false);
      const size = 38 + Math.min(cluster.count, 30); // 개수에 따라 살짝 커짐(39~68px)
      const el = document.createElement('div');
      el.className = 'cluster-marker';
      el.style.cssText = [
        'display:flex',
        'align-items:center',
        'justify-content:center',
        `width:${size}px`,
        `height:${size}px`,
        'border-radius:9999px',
        'background:var(--ca-accent-50,#fff4e6)',
        'border:2px solid var(--ca-accent-line,rgba(226,116,12,.35))',
        'color:var(--ca-accent-dark,#b45309)',
        'font-weight:800',
        `font-size:${size >= 52 ? 14 : 13}px`,
        'font-family:sans-serif',
        'box-shadow:0 4px 12px rgba(60,40,15,.18)',
        'cursor:pointer',
        `transform:scale(${scale})`,
        'transform-origin:center center',
        'transition:transform 0.15s ease-out',
      ].join(';');
      el.textContent = `+${cluster.count}`;
      el.addEventListener('click', () => {
        const target = new kakao.maps.LatLng(cluster.lat, cluster.lng);
        const cur = typeof map.getLevel === 'function' ? map.getLevel() : level;
        map.setLevel(Math.max(cur - 2, 1)); // 줌인(펼침)
        map.panTo(target);
      });
      const overlay = new kakao.maps.CustomOverlay({
        position,
        content: el,
        yAnchor: 0.5,
        xAnchor: 0.5,
        clickable: true,
        zIndex: 50,
      });
      (overlay as any).isCluster = true;
      overlay.setMap(map);
      clusterMarkersRef.current.push(overlay);
    });

    // 분양완료 마커 — 상태 필터와 무관하게 항상, 빨간 '분양완료' 고양이귀(대표: '완판'보다 '분양완료'). 진행 중 현장보다 아래(zIndex 5)에 깔린다.
    //   혜택·추천이 있어도 배너 마커는 쓰지 않는다(분양 중처럼 보이면 안 됨). 묶음(+N)에도 넣지 않는다.
    soldOutRef.current.forEach((property) => {
      if (!property.latitude || !property.longitude) return;
      const el = document.createElement('div');
      el.className = selectedMarkerIdRef.current === property.id ? 'cat-ear-marker sold-out-marker is-selected' : 'cat-ear-marker sold-out-marker';
      el.innerHTML = createCatEarMarkerSVG('분양완료', '분양완료');
      el.title = `${property.title} — 분양완료`;
      el.style.cursor = 'pointer';
      el.style.transform = `scale(${getMarkerScale(currentZoom, false)})`;
      el.style.transformOrigin = 'center bottom';
      el.style.transition = 'transform 0.15s ease-out';
      el.addEventListener('click', () => {
        handleMarkerClickRef.current(property);
      });
      const overlay = new kakao.maps.CustomOverlay({
        position: new kakao.maps.LatLng(property.latitude, property.longitude),
        content: el,
        yAnchor: 1,
        xAnchor: 0.6,
        clickable: true,
        zIndex: 5,
      });
      overlay.setMap(map);
      (overlay as any).propertyData = property;
      (overlay as any).isSpecial = false;
      markersRef.current.push(overlay);
    });
  }, [getMarkerScale]);
  renderMarkersRef.current = renderMarkers;

  // SEO 메타데이터 동적 생성
  const seoData = useMemo(() => {
    const baseUrl = 'https://voonang.com';
    const currentUrl = `${baseUrl}${location.pathname}`;
    const canonicalUrl = generateCanonicalUrl(currentUrl);
    const isNoindex = shouldNoindex(location.search);

    // 지역별 현장 수 계산
    const regionCounts: Record<string, number> = {};
    properties.forEach(p => {
      const region = p.address?.split(' ')[0] || '기타';
      regionCounts[region] = (regionCounts[region] || 0) + 1;
    });

    // 가장 많은 현장이 있는 지역들
    const topRegions = Object.entries(regionCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([region]) => region);

    // 현재 화면의 현장 수 기반 설명
    const visibleCount = visibleProperties.length;
    const totalCount = properties.length;

    const title = '부냥 – 아파트 분양정보, 계약혜택 안내';
    const description = '분양정보, 계약혜택, 분양가, 타입, 분양문의, 청약일정, 모델하우스 정보를 제공합니다.';

    return {
      title,
      description,
      canonicalUrl,
      isNoindex,
      topRegions,
      totalCount,
      visibleCount,
    };
  }, [location.pathname, location.search, properties, visibleProperties]);

  // JSON-LD 구조화 데이터 생성
  const jsonLdData = useMemo(() => {
    const baseUrl = 'https://voonang.com';

    // ItemList 스키마 (지도에 표시된 현장 목록)
    const itemListSchema = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: '부냥 – 아파트 분양정보, 계약혜택 안내',
      description: seoData.description,
      numberOfItems: seoData.visibleCount || seoData.totalCount,
      itemListElement: sortedVisibleProperties.slice(0, 10).map((property, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'RealEstateListing',
          '@id': getPropertyFullUrl(property, baseUrl),
          name: property.title,
          description: `${property.address} - ${property.status}`,
          url: getPropertyFullUrl(property, baseUrl),
          ...(property.latitude && property.longitude && {
            geo: {
              '@type': 'GeoCoordinates',
              latitude: property.latitude,
              longitude: property.longitude,
            },
          }),
          address: {
            '@type': 'PostalAddress',
            streetAddress: property.address,
            addressCountry: 'KR',
          },
        },
      })),
    };

    // WebPage 스키마
    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${baseUrl}/map`,
      name: seoData.title,
      description: seoData.description,
      url: `${baseUrl}/map`,
      isPartOf: {
        '@type': 'WebSite',
        '@id': `${baseUrl}/#website`,
        name: '부냥',
        url: baseUrl,
      },
      about: {
        '@type': 'Thing',
        name: '신규 분양 아파트 지도',
        description: '전국 신규 분양 아파트 및 오피스텔 위치 정보',
      },
      mainEntity: itemListSchema,
    };

    return [webPageSchema, itemListSchema];
  }, [visibleProperties, seoData]);

  useEffect(() => {
    loadProperties();
    loadSettings();
    initializeMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // bfcache(back-forward cache)에서 복원된 경우 강제 리로드
  // 모바일 브라우저에서 뒤로가기/앞으로가기 시 페이지 상태가 복원되어 지도가 안 보이는 문제 해결
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        // bfcache에서 복원된 페이지 - 강제 리로드
        window.location.reload();
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  // 컴포넌트 unmount 시 cleanup (강제 리마운트 시 리소스 정리)
  useEffect(() => {
    // eslint 경고 해결: ref 값을 효과 시작 시 캡처
    const currentMapRef = mapRef.current;

    return () => {
      // 마커 제거
      markersRef.current.forEach(marker => {
        if (marker && marker.setMap) {
          marker.setMap(null);
        }
      });
      markersRef.current = [];

      // 클러스터(＋N) 오버레이 제거 (누수 방지)
      clusterMarkersRef.current.forEach(marker => {
        if (marker && marker.setMap) {
          marker.setMap(null);
        }
      });
      clusterMarkersRef.current = [];

      // 오버레이 제거
      if (overlayRef.current && overlayRef.current.setMap) {
        overlayRef.current.setMap(null);
        overlayRef.current = null;
      }

      // 사용자 위치 마커/오버레이 제거
      if (userLocationMarkerRef.current && userLocationMarkerRef.current.setMap) {
        userLocationMarkerRef.current.setMap(null);
      }
      if (userLocationOverlayRef.current && userLocationOverlayRef.current.setMap) {
        userLocationOverlayRef.current.setMap(null);
      }

      // 맵 인스턴스 정리 (DOM 요소 비우기)
      if (currentMapRef) {
        currentMapRef.innerHTML = '';
      }
      mapInstance.current = null;
    };
  }, []);

  // 페이지 로드 시 위치 권한 요청 (즉시 실행)
  // 단, 저장된 위치가 복원된 경우에는 건너뜀
  useEffect(() => {
    if (locationRequested || !mapLoaded) return;

    // 저장된 위치가 복원된 경우 자동 위치 이동 건너뜀
    if (initialPositionRestoredRef.current) {
      setLocationRequested(true);
      return;
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocationRequested(true);
          // 익명 방문자 통계: 위치 1회 전송 (2026-05-30)
          import('../utils/visitorTracker').then((m) =>
            m.trackVisitorLocation(
              position.coords.latitude,
              position.coords.longitude,
              position.coords.accuracy,
            ),
          );
          // 위치 허용 시 자동으로 내 위치로 이동 (한국 밖이면 기본 대구 뷰 유지)
          if (
            mapInstance.current &&
            window.kakao &&
            isWithinKorea(position.coords.latitude, position.coords.longitude)
          ) {
            const userLatLng = new window.kakao.maps.LatLng(
              position.coords.latitude,
              position.coords.longitude
            );
            mapInstance.current.setCenter(userLatLng);
            mapInstance.current.setLevel(8);
          }
        },
        () => {
          setLocationRequested(true);
        },
        { timeout: 5000 }
      );
    }
  }, [locationRequested, mapLoaded]);

  const loadSettings = async () => {
    try {
      const settings = await settingsApi.get();
      if (settings.marker_icons) {
        setMarkerIcons(settings.marker_icons);
      }
    } catch {
      // 사이트 설정 로드 실패 시 기본 마커 아이콘 사용
    }
  };

  const initializeMap = (retryCount = 0) => {
    const MAX_RETRIES = 100; // 최대 5초 대기 (50ms * 100)

    // SDK가 로드될 때까지 대기 (빠른 재시도)
    if (!window.kakao || !window.kakao.maps) {
      if (retryCount < MAX_RETRIES) {
        setTimeout(() => initializeMap(retryCount + 1), 50);
      }
      return;
    }

    if (!mapRef.current) {
      if (retryCount < MAX_RETRIES) {
        setTimeout(() => initializeMap(retryCount + 1), 50);
      }
      return;
    }

    // 실제 맵 초기화 로직
    const doInitialize = () => {
      try {
        const container = mapRef.current;
        if (!container) {
          if (retryCount < MAX_RETRIES) {
            setTimeout(() => initializeMap(retryCount + 1), 50);
          }
          return;
        }

        // Map 생성자가 아직 없으면 재시도
        if (typeof window.kakao.maps.Map !== 'function') {
          if (retryCount < MAX_RETRIES) {
            setTimeout(() => initializeMap(retryCount + 1), 50);
          }
          return;
        }

        // SPA navigate 시 mount 직후 container height 가 0 인 케이스 (PropertyDetail → /map 등).
        // 0 size 로 init 하면 카카오맵이 빈 화면으로 그려진 채 reflow 가 안 됨 — layout 잡힐 때까지 대기.
        if (container.offsetHeight === 0 || container.offsetWidth === 0) {
          if (retryCount < MAX_RETRIES) {
            setTimeout(() => initializeMap(retryCount + 1), 50);
          }
          return;
        }

        // 저장된 위치가 있으면 복원, 없으면 대구 중심
        const savedPosition = getSavedMapPosition();
        const initialCenter = savedPosition
          ? new window.kakao.maps.LatLng(savedPosition.lat, savedPosition.lng)
          : new window.kakao.maps.LatLng(35.8714, 128.6014); // 대구광역시 중심
        const initialLevel = savedPosition?.level || 8;

        // 위치 복원 여부 기록
        if (savedPosition) {
          initialPositionRestoredRef.current = true;
        }

        const options = {
          center: initialCenter,
          level: initialLevel
        };

        mapInstance.current = new window.kakao.maps.Map(container, options);

        // 지도 빈 공간 클릭 시 팝업 닫기
        window.kakao.maps.event.addListener(mapInstance.current, 'click', () => {
          closeInfoWindow();
        });

        // 지도 이동/줌 시 화면에 보이는 현장만 필터링
        window.kakao.maps.event.addListener(mapInstance.current, 'idle', () => {
          updateVisibleProperties();
        });

        // 줌 변경 시 마커 크기 동적 조정
        window.kakao.maps.event.addListener(mapInstance.current, 'zoom_changed', () => {
          const newZoomLevel = mapInstance.current.getLevel();
          if (newZoomLevel !== currentZoomLevelRef.current) {
            currentZoomLevelRef.current = newZoomLevel;
            updateMarkerScales(newZoomLevel);
            // 줌 변경 시 재클러스터링 (줌인 → 개별로 펼침, 줌아웃 → ＋N 로 묶음)
            if (renderMarkersRef.current) renderMarkersRef.current();
          }
        });

        // 드래그 시작 - 마커 렌더링 최적화 (성능 향상)
        window.kakao.maps.event.addListener(mapInstance.current, 'dragstart', () => {
          if (mapRef.current) {
            mapRef.current.classList.add('map-dragging');
          }
        });

        // 드래그 종료 - 마커 렌더링 복원
        window.kakao.maps.event.addListener(mapInstance.current, 'dragend', () => {
          if (mapRef.current) {
            mapRef.current.classList.remove('map-dragging');
          }
        });

        setMapLoaded(true);

        // SPA navigate 후 layout 변동으로 컨테이너 size 가 바뀐 경우 카카오맵 SDK 가
        // 자동 reflow 하지 않음 — 명시적으로 relayout() 호출하여 빈 화면 회피.
        // 0, 100ms, 300ms 세 단계 — flex 부모 / safe-area-inset 등 늦게 잡히는 환경 대응.
        const relayoutSafely = () => {
          try {
            if (mapInstance.current && typeof mapInstance.current.relayout === 'function') {
              mapInstance.current.relayout();
              const center = mapInstance.current.getCenter();
              if (center) mapInstance.current.setCenter(center);
            }
          } catch {
            /* swallow — 맵 정리 도중 호출되면 무시 */
          }
        };
        relayoutSafely();
        setTimeout(relayoutSafely, 100);
        setTimeout(relayoutSafely, 300);
      } catch (error) {
        // 맵 초기화 실패 시 재시도
        if (retryCount < MAX_RETRIES) {
          setTimeout(() => initializeMap(retryCount + 1), 100);
        }
      }
    };

    // SDK가 이미 완전히 로드된 경우 (Map 생성자가 존재)
    // 페이지 재방문 시 이 경로로 실행됨
    if (typeof window.kakao.maps.Map === 'function') {
      doInitialize();
    } else if (typeof window.kakao.maps.load === 'function') {
      // SDK가 로드 중인 경우 load() 콜백 사용
      window.kakao.maps.load(() => {
        doInitialize();
      });
    } else {
      // SDK가 아직 준비되지 않은 경우 재시도
      if (retryCount < MAX_RETRIES) {
        setTimeout(() => initializeMap(retryCount + 1), 50);
      }
    }
  };

  const loadProperties = async () => {
    try {
      setLoading(true);
      setLoadError(false);
      // 지도 전용 경량 API 사용 (데이터 90% 감소, 로딩 10배 빠름)
      const propertiesList = await propertyApi.getMapData();
      setProperties(propertiesList);
      propertiesRef.current = propertiesList; // ref도 업데이트
      // 완판 마커 목록은 따로 — 실패해도 지도는 정상 동작
      propertyApi.getSoldOutMapData()
        .then((list) => {
          soldOutRef.current = list;
          setSoldOutList(list);
          if (renderMarkersRef.current) renderMarkersRef.current();
        })
        .catch(() => { soldOutRef.current = []; });
    } catch (error) {
      console.error('Failed to load properties:', error);
      setLoadError(true); // 에러 상태 표시 → 빈 지도(정상 0건)와 구분
      setLoadErrorInfo({ code: errorCodeOf(error), at: new Date() });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!mapInstance.current || !mapLoaded || properties.length === 0) return;

    // 마커가 생성되기 전 지도 중심 이동
    // 저장된 위치가 복원된 경우에는 대구로 이동하지 않음
    if (!initialPositionRestoredRef.current) {
      const daeguCenter = new window.kakao.maps.LatLng(35.8714, 128.6014);
      mapInstance.current.setCenter(daeguCenter);
    }

    // 마커/클러스터 렌더 (특별현장 개별 배너 + 일반현장 그리드 클러스터링)
    // 상태 필터·좌표 유효성은 renderMarkers 내부에서 처리한다.
    renderMarkers();

    // 초기 화면에 보이는 현장 필터링
    setTimeout(() => updateVisibleProperties(), 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties, mapLoaded, markerIcons]);

  // URL 쿼리 파라미터로 전달된 현장으로 이동 (?property=id 또는 ?property=house_manage_no)
  useEffect(() => {
    if (!mapLoaded || properties.length === 0) return;

    const searchParams = new URLSearchParams(location.search);
    const propertyIdOrNo = searchParams.get('property');

    if (propertyIdOrNo) {
      // house_manage_no(10자리 이상) 또는 id로 검색
      const targetProperty = propertyIdOrNo.length >= 10
        ? properties.find(p => p.house_manage_no === propertyIdOrNo) || properties.find(p => p.id === parseInt(propertyIdOrNo, 10))
        : properties.find(p => p.id === parseInt(propertyIdOrNo, 10));
      if (targetProperty) {
        // 약간의 딜레이 후 해당 현장으로 이동 (맵 렌더링 완료 대기)
        setTimeout(() => {
          handleMarkerClick(targetProperty);
          // 줌 레벨 조정
          if (mapInstance.current) {
            mapInstance.current.setLevel(5);
          }
        }, 300);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapLoaded, properties, location.search]);

  const handleMarkerClick = (property: Property) => {
    // 배너 마커(활성 혜택 현장)는 바로 상세페이지로 이동 — 마커 종류와 클릭 동작을 일치시킴
    // 완판 마커도 바로 상세로 (상세에 분양완료 표시) — 2026-09-26
    if (usesBannerMarker(property) || property.status === '분양완료') {
      saveMapPosition(); // 지도 위치 저장
      navigate(getPropertyUrl(property));
      return;
    }

    setSelectedProperty(property);
    // v2 선택 마커 강조 (scale 1.1 + 그림자) — 클래스만 토글, 마커 재생성 없음
    selectedMarkerIdRef.current = property.id;
    markersRef.current.forEach((m) => {
      const el = m?.getContent?.();
      if (el?.classList) el.classList.toggle('is-selected', (m as any).propertyData?.id === property.id);
    });
    if (property.latitude && property.longitude && mapInstance.current) {
      const moveLatLng = new window.kakao.maps.LatLng(property.latitude, property.longitude);
      mapInstance.current.setCenter(moveLatLng);

      // 기존 오버레이 제거
      if (overlayRef.current) {
        overlayRef.current.setMap(null);
      }

      // CustomOverlay 생성
      const content = document.createElement('div');
      content.className = 'property-info-window';

      // Close 버튼
      const closeBtn = document.createElement('button');
      closeBtn.className = 'close-btn';
      closeBtn.textContent = '×';
      closeBtn.onclick = () => {
        closeInfoWindow();
      };

      // Info content
      // 정책 (PriceTag 동치): 가격(min/max/실거래)은 어떤 경우에도 노출하지 않음.
      // 라벨 자체 제거 + "가격 문의" 안내로 통일. (1533-8124 액션은 카드 클릭 → 상세)
      const infoContent = document.createElement('div');
      infoContent.className = 'info-content';
      infoContent.innerHTML = `
        <h3>${property.title}</h3>
        <p class="address">${property.address}</p>
        <div class="price-info">
          <span class="price">가격 문의</span>
        </div>
        <div class="details">
          <div class="detail-item">
            <span class="label">전용면적:</span>
            <span>${formatAreaRange(property)}</span>
          </div>
          ${property.total_units ? `
            <div class="detail-item">
              <span class="label">총 세대수:</span>
              <span>${formatUnits(property)}</span>
            </div>
          ` : ''}
        </div>
      `;

      // 상세보기 버튼
      const detailBtn = document.createElement('button');
      detailBtn.className = 'view-detail-btn';
      detailBtn.textContent = '상세보기';
      detailBtn.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        saveMapPosition(); // 지도 위치 저장
        navigate(getPropertyUrl(property));
      };

      // 정보 보기 — 정보밀도 패널 오버레이 열기 (Phase 7, additive).
      // 기존 "상세보기"(→상세페이지)는 그대로 두고, 위에 별도 진입점을 추가한다.
      const infoBtn = document.createElement('button');
      infoBtn.className = 'view-detail-btn';
      infoBtn.textContent = '정보 보기';
      infoBtn.style.backgroundColor = 'var(--ca-accent-50, #fff4e6)';
      infoBtn.style.color = 'var(--ca-accent-dark, #b45309)';
      infoBtn.style.boxShadow = 'none';
      infoBtn.style.border = '1px solid var(--ca-accent-line, rgba(226,116,12,.35))';
      infoBtn.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setPanelPropertyId(property.id);
      };

      infoContent.appendChild(infoBtn);
      infoContent.appendChild(detailBtn);
      content.appendChild(closeBtn);
      content.appendChild(infoContent);

      const overlay = new window.kakao.maps.CustomOverlay({
        content: content,
        position: moveLatLng,
        yAnchor: 1.4,
        clickable: true,
        zIndex: 9999
      });

      overlay.setMap(mapInstance.current);
      overlayRef.current = overlay;

      // 좁은 화면에서 정보창이 지도 밖으로 나가면 그만큼 지도를 밀어 화면 안에 들인다 (여백 12px)
      requestAnimationFrame(() => {
        const map = mapInstance.current;
        if (!map || overlayRef.current !== overlay || !mapRef.current) return;
        const box = content.getBoundingClientRect();
        const area = mapRef.current.getBoundingClientRect();
        // 지도 위에 겹쳐 떠 있는 상단 헤더(로고·검색) 아래까지를 위쪽 한계로 본다
        const header = mapRef.current.closest('.map-page')?.querySelector('header');
        const topEdge = Math.max(area.top, header ? header.getBoundingClientRect().bottom : area.top);
        const M = 12;
        let dx = 0;
        let dy = 0;
        if (box.left < area.left + M) dx = box.left - (area.left + M);
        else if (box.right > area.right - M) dx = box.right - (area.right - M);
        if (box.top < topEdge + M) dy = box.top - (topEdge + M);
        if (dx !== 0 || dy !== 0) map.panBy(Math.round(dx), Math.round(dy));
      });
    }
  };
  // 이벤트 리스너 클로저(renderMarkers)에서 최신 핸들러를 참조하도록 ref 동기화
  handleMarkerClickRef.current = handleMarkerClick;

  const closeInfoWindow = () => {
    setSelectedProperty(null);
    selectedMarkerIdRef.current = null;
    markersRef.current.forEach((m) => {
      const el = m?.getContent?.();
      if (el?.classList) el.classList.remove('is-selected');
    });
    if (overlayRef.current) {
      overlayRef.current.setMap(null);
      overlayRef.current = null;
    }
  };

  // 화면에 보이는 현장만 필터링
  const updateVisibleProperties = useCallback(() => {
    if (!mapInstance.current || propertiesRef.current.length === 0) return;

    const bounds = mapInstance.current.getBounds();
    const sw = bounds.getSouthWest(); // 남서쪽 좌표
    const ne = bounds.getNorthEast(); // 북동쪽 좌표
    const currentFilter = statusFilterRef.current;

    const visible = propertiesRef.current.filter(property => {
      // 사용자 피드백 2026-05-22: 일반현장도 viewport 안에 있으면 list/마커 노출.
      if (!property.latitude || !property.longitude) return false;

      // 마커 생성 로직과 동일하게 business_type 우선 적용 + 입주완료 자동 판단
      const isRental = property.business_type === '임대';
      const isMixed = property.business_type === '분양임대';
      const displayStatus = getDisplayStatus(property);
      const markerStatus = isMixed ? '분양임대' : (isRental ? '임대' : displayStatus);

      // 상태 필터 적용 (빈 배열 = 전체, 배열에 포함된 상태만 표시)
      // 특별현장·배너는 renderMarkers 와 동일하게 필터 제외 (2026-09-22: 마커는 보이는데 카드가 없던 불일치 수정)
      const filterExempt = property.is_special || usesBannerMarker(property);
      if (!filterExempt && currentFilter.length > 0 && !currentFilter.includes(markerStatus)) {
        return false;
      }

      return (
        property.latitude >= sw.getLat() &&
        property.latitude <= ne.getLat() &&
        property.longitude >= sw.getLng() &&
        property.longitude <= ne.getLng()
      );
    });

    setVisibleProperties(visible);
  }, []);

  // 상태 필터 변경 핸들러 (다중선택 토글)
  const handleStatusFilterChange = useCallback((clickedStatus: string) => {
    let newFilter: string[];

    if (clickedStatus === '전체') {
      // 전체 클릭 시 모든 필터 해제 (빈 배열 = 전체 표시)
      newFilter = [];
    } else {
      // 개별 카테고리 토글
      const currentFilter = statusFilterRef.current;
      if (currentFilter.includes(clickedStatus)) {
        // 이미 선택된 경우 제거
        newFilter = currentFilter.filter(s => s !== clickedStatus);
      } else {
        // 선택되지 않은 경우 추가
        newFilter = [...currentFilter, clickedStatus];
      }
    }

    setStatusFilter(newFilter);
    statusFilterRef.current = newFilter;

    // 마커/클러스터 재렌더 — 상태 필터 반영 후 다시 클러스터링
    // (특별현장은 필터 제외·항상 노출, 일반현장만 필터 적용은 renderMarkers 내부 처리)
    if (renderMarkersRef.current) renderMarkersRef.current();

    // visibleProperties 업데이트
    updateVisibleProperties();
  }, [updateVisibleProperties]);

  // 내 위치 표시 기능
  const showUserLocation = () => {
    if (!mapInstance.current || !mapLoaded) {
      return;
    }

    if (!navigator.geolocation) {
      alert('이 브라우저는 위치 서비스를 지원하지 않습니다.');
      return;
    }

    setLocatingUser(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        // 익명 방문자 통계: 위치를 백엔드에 1회 전송 (24h dedup) - 2026-05-30
        import('../utils/visitorTracker').then((m) =>
          m.trackVisitorLocation(latitude, longitude, accuracy),
        );
        // 한국 밖 위치면 지도 이동하지 않음 (카카오맵 타일 없는 지역 → 백지 방지)
        if (!isWithinKorea(latitude, longitude)) {
          setLocatingUser(false);
          alert('현재 위치가 서비스 지역(대한민국) 밖이에요. 지도는 대구·경북 지역을 보여드립니다.');
          return;
        }
        const userPosition = new window.kakao.maps.LatLng(latitude, longitude);

        // 기존 사용자 위치 마커/오버레이 제거
        if (userLocationMarkerRef.current) {
          userLocationMarkerRef.current.setMap(null);
        }
        if (userLocationOverlayRef.current) {
          userLocationOverlayRef.current.setMap(null);
        }

        // 사용자 위치 마커 생성 (파란색 원형)
        const markerContent = document.createElement('div');
        markerContent.className = 'user-location-marker';
        markerContent.innerHTML = `
          <div class="user-location-pulse"></div>
          <div class="user-location-dot"></div>
        `;

        const userMarkerOverlay = new window.kakao.maps.CustomOverlay({
          content: markerContent,
          position: userPosition,
          yAnchor: 0.5,
          xAnchor: 0.5,
          zIndex: 100
        });

        userMarkerOverlay.setMap(mapInstance.current);
        userLocationMarkerRef.current = userMarkerOverlay;

        // 지도 중심 이동
        mapInstance.current.setCenter(userPosition);
        mapInstance.current.setLevel(5); // 더 가깝게 줌

        // 위치 이름 표시 (역지오코딩)
        if (window.kakao.maps.services) {
          const geocoder = new window.kakao.maps.services.Geocoder();
          geocoder.coord2Address(longitude, latitude, (result: any, status: any) => {
            if (status === window.kakao.maps.services.Status.OK && result[0]) {
              const address = result[0].address;
              const locationName = `${address.region_2depth_name} ${address.region_3depth_name}`;

              // 위치 정보 오버레이 생성
              const infoContent = document.createElement('div');
              infoContent.className = 'user-location-info';
              infoContent.innerHTML = `
                <div class="user-location-info-content">
                  <span class="location-icon">📍</span>
                  <span class="location-text">내 위치: ${locationName}</span>
                </div>
              `;

              const infoOverlay = new window.kakao.maps.CustomOverlay({
                content: infoContent,
                position: userPosition,
                yAnchor: 2.5,
                xAnchor: 0.5,
                zIndex: 101
              });

              infoOverlay.setMap(mapInstance.current);
              userLocationOverlayRef.current = infoOverlay;

              // 5초 후 위치 정보 오버레이 제거
              setTimeout(() => {
                if (userLocationOverlayRef.current) {
                  userLocationOverlayRef.current.setMap(null);
                }
              }, 5000);
            }
          });
        }

        setLocatingUser(false);
        if (onMyLocationShown) {
          onMyLocationShown();
        }
      },
      (error) => {
        setLocatingUser(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            alert('위치 권한이 거부되었습니다. 설정에서 위치 권한을 허용해주세요.');
            break;
          case error.POSITION_UNAVAILABLE:
            alert('위치 정보를 사용할 수 없습니다.');
            break;
          case error.TIMEOUT:
            alert('위치 정보 요청 시간이 초과되었습니다.');
            break;
          default:
            alert('위치를 가져오는데 실패했습니다.');
        }
        if (onMyLocationShown) {
          onMyLocationShown();
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  };

  // showMyLocation prop 변화 감지
  useEffect(() => {
    if (showMyLocation && mapLoaded) {
      showUserLocation();
    }
  }, [showMyLocation, mapLoaded]);

  // 페이지 리로드 후 sessionStorage에서 위치 표시 플래그 확인
  useEffect(() => {
    if (mapLoaded) {
      const shouldShowLocation = sessionStorage.getItem('showMyLocation');
      if (shouldShowLocation === 'true') {
        sessionStorage.removeItem('showMyLocation');
        showUserLocation();
      }
    }
  }, [mapLoaded]);

  // 정책 (Phase 7): 가격 포맷터 제거 — InfoWindow가 "가격 문의"로 통일됨
  // formatPrice, formatPriceRange 함수는 더 이상 호출처 없음 → 죽은 코드 제거

  const getStatusClass = (status: string) => {
    switch (status) {
      case '분양예정': return 'status-upcoming';
      case '분양중': return 'status-active';
      case '입주완료': return 'status-movedin'; // 입주완료 (회색)
      case '분양완료': return 'status-completed';
      case '계약완료': return 'status-closed';
      default: return 'status-active';
    }
  };

  // ──────────────────────────────────────────
  // Concept A UI 헬퍼
  // ──────────────────────────────────────────
  // admin 관리 필터칩 (사용자 피드백 2026-05-19) — /api/map-filters/ 동적 로드
  // statusFilter 는 기존 카카오맵 마커 렌더링용으로 유지, 이 chip 은 시트 카드 필터링용.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mapChips, setMapChips] = useState<MapFilterChip[]>([]);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mapChipActiveIds, setMapChipActiveIds] = useState<Set<number>>(new Set());

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    let cancelled = false;
    mapFilterApi.list().then((chips) => {
      if (!cancelled) setMapChips(chips);
    }).catch(() => {
      // 백엔드 미응답 시 빈 리스트로 진행 (전체만 노출)
    });
    return () => { cancelled = true; };
  }, []);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const toggleMapChip = useCallback((id: number) => {
    setMapChipActiveIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const noChipActive = mapChipActiveIds.size === 0;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [sheetSnap, setSheetSnap] = useState<'collapsed' | 'mid' | 'expanded'>('mid');
  // "이 영역 다시 검색" 버튼 폐기 (사용자 피드백 2026-05-19):
  // 지도 이동/zoom 시 updateVisibleProperties 가 자동 호출되어 카드 리스트가
  // 즉시 갱신되므로 명시적 재검색 버튼 불필요

  // Phase 7: viewport 변화 시 카카오맵 컨테이너 크기 적응 (relayout)
  // lg threshold(1024) 통과 시 컨테이너 폭이 lg:pl-[404px]로 변하므로 지도가 자동 재배치되어야 함.
  // 카카오맵 SDK는 parent resize를 자동 감지 안 함 — 명시적 relayout() + setCenter 필요.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (!mapLoaded || !mapInstance.current || !(window as any).kakao) return;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const handleResize = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const map = mapInstance.current;
        if (!map) return;
        try {
          const center = map.getCenter();
          // 카카오맵 relayout — 컨테이너 크기 변화 후 호출 필수
          if (typeof map.relayout === 'function') map.relayout();
          // center를 다시 설정해서 시각적 위치 유지
          map.setCenter(center);
        } catch {}
      }, 200);
    };
    window.addEventListener('resize', handleResize);
    // 초기 1회도 호출 (페이지 로드 시 viewport에 맞춤)
    handleResize();
    return () => {
      window.removeEventListener('resize', handleResize);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [mapLoaded]);

  const sheetCards = useMemo(() => {
    let list = [...specialProperties, ...sortedVisibleProperties];
    // 활성 chip (mapChipActiveIds 에 들어있는 chip 의 filter 만 적용)
    mapChips.forEach((chip) => {
      if (mapChipActiveIds.has(chip.id)) {
        list = applyChipFilter(list, chip);
      }
    });
    return list;
  }, [specialProperties, sortedVisibleProperties, mapChips, mapChipActiveIds]);

  // sheetRegionLabel 폐기 — 사용자 피드백 2026-05-19: 지역명 노출 안 함

  useSeoMeta({
    title: seoData.title,
    description: seoData.description,
    robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
    ogType: 'website',
    ogTitle: seoData.title,
    ogDescription: seoData.description,
    ogUrl: 'https://voonang.com/',
    ogSiteName: '부냥',
    ogLocale: 'ko_KR',
    twitterCard: 'summary',
    twitterTitle: seoData.title,
    twitterDescription: seoData.description,
  });
  useHead({
    meta: [
      { name: 'keywords', content: '부냥, 신규분양, 아파트분양, 오피스텔분양, 분양정보, 청약정보, 신규아파트, 분양혜택' },
    ],
    // /(홈)과 /map 은 동일 콘텐츠 → canonical 을 항상 홈으로 고정해 중복 색인 방지 (sitemap 대표 URL)
    link: [{ rel: 'canonical', href: 'https://voonang.com/' }],
    script: jsonLdData.map((schema) => ({
      type: 'application/ld+json',
      innerHTML: JSON.stringify(schema),
    })),
  });

  // 헤더 우측 "현재 위치" 버튼 핸들러 — mapInstance ref 접근이라 부모에 유지
  const handleHeaderLocate = () => {
    // 사용자 위치 요청 — App.tsx에서 showMyLocation prop 전달, 또는 reload trigger.
    // 기존 로직: showMyLocation prop이 true가 되면 위치 fetch.
    if (navigator.geolocation && mapInstance.current && (window as any).kakao) {
      setLocatingUser(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          // 한국 밖 위치면 이동하지 않음 (백지 지도 방지)
          if (isWithinKorea(latitude, longitude)) {
            const center = new (window as any).kakao.maps.LatLng(latitude, longitude);
            mapInstance.current.setCenter(center);
          }
          setLocatingUser(false);
        },
        () => setLocatingUser(false),
        { enableHighAccuracy: true, timeout: 8000 },
      );
    }
  };

  return (
    <div className="map-page bg-paper">
      {/* SEO·접근성: 렌더 DOM 상 페이지 대표 제목 (h1). JS 실행 크롤러·스크린리더용,
          시각적으로는 숨김(sr-only) — 지도 UI 레이아웃에 영향 없음. */}
      <h1 className="sr-only">부냥 – 대구·경북 아파트 신규분양 정보</h1>
      {/* Map Container — 모바일 풀폭 / 데스크탑 lg+ 좌측 404px 패널 + 우측 지도
          .map-page (부모) 가 명시 height (calc 100vh - 2vh / 모바일 -58px) 잡음.
          여기는 flex-1 로 그 영역 채움 */}
      <div className="relative flex-1 w-full bg-paper lg:pl-[404px]" style={{ position: 'relative' }}>

        {/* 데스크탑 좌측 패널 (lg+ 전용) — 검색·chip·세로 카드 리스트 */}
        <SidebarPanel
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          setSearchFocused={setSearchFocused}
          searchSuggestions={searchSuggestions}
          showSearchDropdown={showSearchDropdown}
          handleSuggestionSelect={handleSuggestionSelect}
          mapChips={mapChips}
          noChipActive={noChipActive}
          mapChipActiveIds={mapChipActiveIds}
          setMapChipActiveIds={setMapChipActiveIds}
          toggleMapChip={toggleMapChip}
          sheetCards={sheetCards}
          favoriteIds={favoriteIds}
          handleToggleFavorite={handleToggleFavorite}
          handleMarkerClick={handleMarkerClick}
          selectedProperty={selectedProperty}
        />


        {/* 1. StickyHeader — 로고 (전 viewport) + DesktopNav (md+) + 검색 (center) + 우측 액션
            사용자 피드백 (2026-05-19): 모바일 "뒤로" 버튼 제거 (메인 = 지도이므로 뒤로갈 곳 없음).
            로고로 교체. */}
        <MapHeader
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          setSearchFocused={setSearchFocused}
          searchSuggestions={searchSuggestions}
          showSearchDropdown={showSearchDropdown}
          handleSuggestionSelect={handleSuggestionSelect}
          onLocateClick={handleHeaderLocate}
        />

        {/* 2. Floating 카테고리 chip row — 헤더 바로 아래. lg+ 숨김.
            admin 에 활성 chip 이 1개 이상일 때만 노출 */}
        {mapChips.length > 0 && (
        <div className="absolute inset-x-0 top-[58px] z-[13] lg:hidden">
          <div
            className="flex gap-1.5 overflow-x-auto px-3.5 py-2"
            style={{ scrollbarWidth: 'none' }}
          >
            <FilterChip
              label="전체"
              active={noChipActive}
              onClick={() => setMapChipActiveIds(new Set())}
            />
            {mapChips.map((chip) => (
              <FilterChip
                key={chip.id}
                label={chip.label}
                active={mapChipActiveIds.has(chip.id)}
                emphasis={chip.emphasis}
                onClick={() => toggleMapChip(chip.id)}
              />
            ))}
          </div>
        </div>
        )}

        {/* 4. 지도 — mapRef 그대로 유지 (카카오맵 SDK 통합) */}
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-paper text-muted text-[13px]">
            지도 로딩 중...
          </div>
        ) : (
          <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
        )}

        {/* 데이터 로드 실패 시 — v2 S3 (다시 시도 + 전화 + 오류코드·시각). 빈 지도(정상 0건)와 구분 */}
        {!loading && loadError && (
          <div className="absolute left-1/2 top-1/2 z-[13] w-[min(360px,calc(100%-32px))] -translate-x-1/2 -translate-y-1/2 rounded-ca-3xl bg-frame/97 shadow-ca-sheet backdrop-blur">
            <ErrorState
              className="!py-8 !px-5"
              onRetry={() => loadProperties()}
              code={loadErrorInfo?.code}
              at={loadErrorInfo?.at}
            />
          </div>
        )}

        {/* 5. 위치 검색 중 오버레이 */}
        {locatingUser && (
          <div className="absolute left-1/2 top-1/2 z-[12] -translate-x-1/2 -translate-y-1/2 flex items-center gap-2 rounded-ca-xl border border-ca-line bg-frame/95 px-3.5 py-2.5 shadow-ca-card backdrop-blur">
            <div className="h-3 w-3 animate-spin rounded-full border-2 border-accent border-t-transparent" />
            <span className="text-[12.5px] font-semibold text-ink">현재 위치를 찾고 있어요...</span>
          </div>
        )}

        {/* 6. "이 영역 다시 검색" 버튼 폐기 — 사용자 피드백 2026-05-19
              (지도 이동/zoom 시 자동 리스트 갱신되므로 수동 재검색 불필요) */}

        {/* 7. Zoom controls (우측) — v2 `.mapctl`: 우측 12px · 36px 정사각 · radius 10 · gap 6 */}
        <div className="absolute right-3 bottom-[44%] z-[11] flex flex-col gap-1.5">
          <button
            type="button"
            onClick={() => {
              const map = mapInstance.current;
              if (map) map.setLevel(map.getLevel() - 1);
            }}
            className="flex h-9 w-9 items-center justify-center rounded-ca-lg bg-frame/95 text-body shadow-ca-card"
            aria-label="확대"
          >
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" aria-hidden>
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => {
              const map = mapInstance.current;
              if (map) map.setLevel(map.getLevel() + 1);
            }}
            className="flex h-9 w-9 items-center justify-center rounded-ca-lg bg-frame/95 text-body shadow-ca-card"
            aria-label="축소"
          >
            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" aria-hidden>
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        </div>

        {/* 8. BottomSheet — 모바일(< lg) 하단 캐러셀 시트. 2026-05-19 hidden → 2026-09-22 대표 결정으로 v2 목업 03 대로 노출.
            지도 중심 유지: 높이는 카드 1줄만(접으면 헤더만). lg+ 는 좌측 패널이 카드 리스트 담당 */}
        <BottomSheet
          sheetSnap={sheetSnap}
          setSheetSnap={setSheetSnap}
          sheetCards={sheetCards}
          selectedProperty={selectedProperty}
          handleMarkerClick={handleMarkerClick}
          handleToggleCompare={handleToggleCompare}
          compareIds={compareIds}
          favoriteIds={favoriteIds}
          handleToggleFavorite={handleToggleFavorite}
        />
      </div>

      {/* 플로팅 비교 바 — 비교함에 1개 이상 담기면 노출 (2026-07-07).
          2026-09-22: < lg 는 하단 시트를 가리므로 숨기고 시트 헤더의 '단지 비교' 로 대체 */}
      <div className="hidden lg:block">
        <CompareFloatingBar compareIds={compareIds} />
      </div>

      {/* 정보밀도 패널 (Phase 7, additive) — 마커 정보창 "정보 보기"로 열림.
          지도/마커/바텀시트 위에 오버레이로만 얹고, 닫으면 지도 그대로 복귀. */}
      {panelPropertyId !== null && (
        <PropertyPanel propertyId={panelPropertyId} onClose={() => setPanelPropertyId(null)} />
      )}
    </div>
  );
};

export default MapPage;
